<template>
  <div class="d-flex align-center justify-end">
    <!-- Stock status indicators with icons instead of text -->
    <v-tooltip location="top" v-if="isOutOfStock">
      <template v-slot:activator="{ props }">
        <v-icon v-bind="props" class="mr-1" color="error" :size="iconSize">
          mdi-package-variant-remove
        </v-icon>
      </template>
      <span>{{ $t("inventory.outOfStock") }}</span>
    </v-tooltip>

    <v-tooltip location="top" v-else-if="isLowStock(getStockCount, getLowStockThreshold)">
      <template v-slot:activator="{ props }">
        <v-icon v-bind="props" class="mr-1" color="warning" :size="iconSize">
          mdi-alert-circle-outline
        </v-icon>
      </template>
      <span>{{ $t("inventory.lowStock") }}</span>
    </v-tooltip>

    <!-- Stock count chip with low stock threshold and oversell limit tooltip -->
    <v-tooltip location="top">
      <template v-slot:activator="{ props }">
        <v-chip
          :color="
            getStockColor(
              getStockCount,
              getLowStockThreshold,
              getCanOversell,
              getInventoryLowerBound,
            )
          "
          :size="size"
          class="mr-2 font-weight-bold"
          v-bind="props"
        >
          {{ getStockCount !== null && getStockCount !== undefined ? getStockCount : "-" }}
        </v-chip>
      </template>
      <span>{{ stockTooltipText }}</span>
    </v-tooltip>

    <!-- Checkout count chip -->
    <v-chip
      color="blue-lighten-2"
      :size="size === 'default' ? 'small' : 'x-small'"
      class="checkout-chip"
    >
      {{ getCheckoutCount || 0 }}
      <v-icon size="x-small" class="ml-1" color="blue-darken-1">mdi-cart-outline</v-icon>
    </v-chip>
  </div>
</template>

<script lang="ts" setup>
import { defineProps, computed, onMounted, onUnmounted, ref, watch } from "vue";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/app/plugins/firebase";
import { useAccountStore } from "@/account/stores/account";
import i18n from "@/app/plugins/i18n";

const props = defineProps({
  // Accept a product object (preferred method for future use)
  product: {
    type: Object,
    default: null,
  },
  // Optional location context for location-based inventory
  // When provided, we listen to accounts/:accountId/locations/:locationId/products/:productId
  locationId: {
    type: String,
    default: undefined,
  },
  // For backward compatibility with components that pass individual props
  // These props will be used if product object is not provided
  stockCount: {
    type: Number,
    default: null,
  },
  lowStockThreshold: {
    type: Number,
    default: null,
  },
  checkoutCount: {
    type: Number,
    default: 0,
  },
  size: {
    type: String,
    default: "default", // 'small', 'x-small', etc.
    validator: (value: string) =>
      ["x-small", "small", "default", "large", "x-large"].includes(value),
  },
});

// Reactive state variables for Firebase data
const localStockCount = ref<number | null>(null);
const localLowStockThreshold = ref<number | null>(null);
const localCheckoutCount = ref<number | null>(null);
const localCanOversell = ref<boolean | undefined>(undefined);
const localInventoryLowerBound = ref<number | null | undefined>(undefined);
let unsubscribe: (() => void) | null = null;

const accountStore = useAccountStore();

// Icon size computed property based on the chip size
const iconSize = computed(() => {
  switch (props.size) {
    case "x-small":
      return "x-small";
    case "small":
      return "small";
    case "large":
      return "medium";
    case "x-large":
      return "large";
    default:
      return "small";
  }
});

// Get accountId from the store
const accountId = computed(() => accountStore.account?.id);

const locationBasedInventoryEnabled = computed(() => {
  const account = accountStore.account;
  return account?.accountSettings?.inventory?.location_based_inventory_enabled === true;
});

// Setup Firebase realtime listener for product data
const setupFirebaseListener = () => {
  // Make sure we have an account ID and product ID
  if (!accountId.value || !props.product?.id) {
    return;
  }

  try {
    const productId = props.product.id;
    const locationId =
      locationBasedInventoryEnabled.value &&
      typeof props.locationId === "string" &&
      props.locationId
        ? props.locationId
        : null;

    const productDocRef = locationId
      ? doc(db, "accounts", accountId.value, "locations", locationId, "products", productId)
      : doc(db, "accounts", accountId.value, "products", productId);

    unsubscribe = onSnapshot(
      productDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();

          // Update local state with the latest data from Firebase
          if (data.stockCount !== undefined) localStockCount.value = data.stockCount;
          if (data.lowStockThreshold !== undefined)
            localLowStockThreshold.value = data.lowStockThreshold;
          if (data.checkoutCount !== undefined) localCheckoutCount.value = data.checkoutCount;
          if (data.canOversell !== undefined) localCanOversell.value = data.canOversell === true;
          if (data.inventoryLowerBound !== undefined)
            localInventoryLowerBound.value = data.inventoryLowerBound;
        }
      },
      (error) => {
        console.error(`Error listening to product data:`, error);
      },
    );
  } catch (error) {
    console.error("Error setting up Firebase listener:", error);
  }
};

const resetFirebaseListener = () => {
  // Clean up existing listener if there is one
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }

  // Reset local state so we don't show stale values when changing products/locations
  localStockCount.value = null;
  localLowStockThreshold.value = null;
  localCheckoutCount.value = null;
  localCanOversell.value = undefined;
  localInventoryLowerBound.value = undefined;

  if (props.product?.id) {
    setupFirebaseListener();
  }
};

// Watch for changes in the product or location context
watch(
  () => [props.product?.id, props.locationId, locationBasedInventoryEnabled.value],
  () => {
    resetFirebaseListener();
  },
  { immediate: true },
);

// For debugging in development
onMounted(() => {
  if (process.env.NODE_ENV === "development") {
    if (!props.product && (props.stockCount === null || props.stockCount === undefined)) {
      console.warn(
        "[ProductStockChips] Warning: Neither product object nor stockCount was provided",
      );
    }

    if (props.product) {
      if (props.product.stockCount === undefined && process.env.NODE_ENV === "development") {
        console.warn(
          "[ProductStockChips] Product object provided but stockCount property is undefined",
        );
      }
    }
  }
});

// Clean up the listener when component is unmounted
onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
});

// Computed properties to handle both Firebase data and props
const getStockCount = computed(() => {
  // If we have data from Firebase, use that first
  if (localStockCount.value !== null) {
    return localStockCount.value;
  }

  const isLocationContext =
    locationBasedInventoryEnabled.value &&
    typeof props.locationId === "string" &&
    props.locationId.length > 0;
  if (isLocationContext) {
    // In location context, don't fall back to the global product stock count.
    // If we don't have a location-specific value, show '-'.
    if (props.stockCount !== null && props.stockCount !== undefined) {
      return props.stockCount;
    }
    return null;
  }

  // Otherwise fall back to props
  if (props.product) {
    return props.product.stockCount !== undefined ? props.product.stockCount : null;
  }
  return props.stockCount;
});

const getLowStockThreshold = computed(() => {
  // If we have data from Firebase, use that first
  if (localLowStockThreshold.value !== null) {
    return localLowStockThreshold.value;
  }

  const isLocationContext =
    locationBasedInventoryEnabled.value &&
    typeof props.locationId === "string" &&
    props.locationId.length > 0;
  if (isLocationContext) {
    if (props.lowStockThreshold !== null && props.lowStockThreshold !== undefined) {
      return props.lowStockThreshold;
    }
    return null;
  }

  // Otherwise fall back to props
  if (props.product) {
    return props.product.lowStockThreshold !== undefined ? props.product.lowStockThreshold : null;
  }
  return props.lowStockThreshold;
});

const getCheckoutCount = computed(() => {
  // If we have data from Firebase, use that first
  if (localCheckoutCount.value !== null) {
    return localCheckoutCount.value;
  }

  const isLocationContext =
    locationBasedInventoryEnabled.value &&
    typeof props.locationId === "string" &&
    props.locationId.length > 0;
  if (isLocationContext) {
    if (props.checkoutCount !== null && props.checkoutCount !== undefined) {
      return props.checkoutCount;
    }
    return null;
  }

  // Otherwise fall back to props
  if (props.product) {
    return props.product.checkoutCount || 0;
  }
  return props.checkoutCount || 0;
});

// Utility functions for stock display
const isOutOfStockByLowerBound = (
  stockCount: number | null | undefined,
  canOversell: boolean,
  inventoryLowerBound: number | null | undefined,
): boolean => {
  if (stockCount === null || stockCount === undefined) {
    return false;
  }

  if (canOversell && inventoryLowerBound === null) {
    return false;
  }

  const lowerBound = canOversell ? (inventoryLowerBound ?? 0) : 0;
  return Number(stockCount) <= lowerBound;
};

const isOutOfStock = computed(() =>
  isOutOfStockByLowerBound(getStockCount.value, getCanOversell.value, getInventoryLowerBound.value),
);

const getStockColor = (
  stockCount: number | null | undefined,
  threshold: number | null | undefined,
  canOversell: boolean,
  inventoryLowerBound: number | null | undefined,
): string => {
  // If stockCount is null or undefined, return grey
  if (stockCount === null || stockCount === undefined) {
    return "grey";
  }

  const count = Number(stockCount);

  if (isOutOfStockByLowerBound(count, canOversell, inventoryLowerBound)) {
    return "error";
  }

  if (threshold && count < Number(threshold)) {
    return "warning";
  }

  return "success";
};

const isLowStock = (
  stockCount: number | null | undefined,
  threshold: number | null | undefined,
): boolean => {
  if (stockCount === null || stockCount === undefined) {
    return false;
  }

  const count = Number(stockCount);
  const thresholdNum = Number(threshold);

  return (
    !isOutOfStockByLowerBound(stockCount, getCanOversell.value, getInventoryLowerBound.value) &&
    thresholdNum > 0 &&
    count < thresholdNum
  );
};

const getCanOversell = computed(() => {
  if (localCanOversell.value !== undefined) {
    return localCanOversell.value;
  }

  return props.product?.canOversell === true;
});

const getInventoryLowerBound = computed(() => {
  if (!getCanOversell.value) {
    return 0;
  }

  if (localInventoryLowerBound.value !== undefined) {
    return localInventoryLowerBound.value;
  }

  if (props.product && Object.prototype.hasOwnProperty.call(props.product, "inventoryLowerBound")) {
    return props.product.inventoryLowerBound === null
      ? null
      : typeof props.product.inventoryLowerBound === "number"
        ? props.product.inventoryLowerBound
        : 0;
  }

  return 0;
});

const oversellLimitText = computed(() => {
  if (getInventoryLowerBound.value === null) {
    return "∞";
  }
  if (getInventoryLowerBound.value === 0) {
    return i18n.global.t("inventory.noOverselling");
  }
  return String(getInventoryLowerBound.value);
});

const stockTooltipText = computed(() => {
  const thresholdText = getLowStockThreshold.value
    ? `${i18n.global.t("inventory.lowStockThreshold")}: ${getLowStockThreshold.value}. `
    : "";
  return `${thresholdText}${i18n.global.t("inventory.oversellLimit")}: ${oversellLimitText.value}`;
});
</script>

<style scoped>
/* Checkout chip styling */
.checkout-chip {
  border: 1px solid #bbdefb;
}

.checkout-chip .v-chip__content {
  font-weight: 500;
  color: #1976d2;
}
</style>
